import React from 'react';
import {
	Bank,
	CredentialType,
	TransactionsLabel,
	TransactionStatus,
	TransactionType,
	TransactionTypeAliasAuthenticationMessageOperationInputDto,
} from '@api/izibizi-xp';
import { useShowHide, useTranslation } from '@bcp-channels-sharedcomponents-reactjs/core/dist/hooks';
import { useCountdown } from '@bcp-nextgen-dx-component-factory/accolade-design-system';
import { useLogger, useSubscription } from '@hooks';
import { useNavigation } from '@navigation';
import { useCallback, useMemo, useRef, useState } from 'react';
import { useFetchAuthenticatorMessage, useFetchAuthenticatorType } from '@services';
import { CoreHelper } from '@bcp-channels-sharedcomponents-reactjs/core/dist/helpers';
import { modals } from '@modals';
import { useNavigationStore, useSelectedSolution, useUserInput } from '@store';

interface AuthenticatorFooterResources {
	linkBack: string;
	title: string;
	subtitle: string;
	typeAuthenticationSMS: string;
	authenticationTitle: string;
	description: string;
	message: string;
	askAnotherCode: string;
	validateButton: string;
	errorMessage: string;
}

export const useAuthenticatorHelper = () => {
	const t = useTranslation();
	const logger = useLogger();
	const { back, conclusionIziBizi } = useNavigation();
	const { hide, show } = useShowHide(modals.validation);

	const { flow } = useNavigationStore();

	const [showAuthentication, setShowAuthentication] = useState<boolean>(false);
	const [buttonDisableValidate, setButtonDisableValidate] = useState<boolean>(true);
	const [showMessageError, setShowMessageError] = useState<boolean>(false);
	const [token, setToken] = useState<string>('');
	const [loading, setLoading] = useState(true);

	const { isValid: isValidStore, selectedAccount } = useUserInput();
	const selectedSolution = useSelectedSolution();
	const { fetch: fetchAuthenticatorMessage } = useFetchAuthenticatorMessage();
	const { fetchSubscription } = useSubscription({ token: token });
	const isAuthValid = React.useRef(false);

	const isValid: boolean = useMemo(() => {
		return flow === 'subscription' ? (isValidStore === undefined ? false : isValidStore) : true;
	}, [flow, isValidStore]);

	const { success } = useFetchAuthenticatorType();

	React.useEffect(() => {
		// hide loading when ending fetch of authenticator type
		if (success !== undefined) {
			setLoading(false);
		}
	}, [success]);

	React.useEffect(() => {
		setButtonDisableValidate(!isAuthValid.current || !isValid);
	}, [isValid]);

	const labelFromPhone = useRef<string>('');
	const waitingTime = useMemo(() => {
		const time = parseInt(t('placeholders.authenticatorFooter.authenticator.waitingTime'));
		return !isNaN(time) ? time : 60;
	}, [t]);

	const { timer$, time, canResetCountdown: canRequestNewCode } = useCountdown(waitingTime);

	const resources = useMemo((): AuthenticatorFooterResources => {
		return {
			authenticationTitle: t('placeholders.authenticatorFooter.authenticator.title'),
			description: t('placeholders.authenticatorFooter.authenticator.description', {
				phone: labelFromPhone.current,
			}),
			message: t('placeholders.authenticatorFooter.authenticator.timeUntilToAskAnotherCode', {
				time,
			}),
			askAnotherCode: t('placeholders.authenticatorFooter.authenticator.askForAnotherCode'),
			linkBack: t('general.back'),
			title: t('placeholders.authenticatorFooter.footer.title'),
			subtitle: t('placeholders.authenticatorFooter.footer.subtitle'),
			typeAuthenticationSMS: t('placeholders.authenticatorFooter.footer.typeAuthenticationSMS'),
			validateButton: t('general.validate'),
			errorMessage: t('placeholders.authenticatorFooter.authenticator.errorMessage'),
		};
	}, [t, time]);

	const restartTime = React.useCallback(() => {
		timer$.next({ value: waitingTime });
	}, [timer$, waitingTime]);

	const showHideLoading = () => setLoading((old) => !old);

	const requestAuthentication = () => {
		let transactionType: TransactionType = TransactionType.IziBizi_Subscription;
		let transactionLabels: TransactionsLabel[] | null = null;
		if (flow === 'integratedSolution') {
			transactionType = TransactionType.IziBizi_IntegratedSolution_Subscription;
			transactionLabels = [
				{
					transactionLabelId: 2,
					transactionLabelVersionId: 0,
					labelValue: selectedSolution?.name,
				},
			];
		}
		const data: TransactionTypeAliasAuthenticationMessageOperationInputDto = {
			accountId: selectedAccount?.account.id,
			bank: Bank.MillenniumBcp,
			credentialType: CredentialType.Sms,
			language: 'PT',
			transaction: transactionType,
			transactionLabels: transactionLabels,
			transactionAmount: 0,
			transactionCurrency: 'EUR',
		};
		showHideLoading();
		fetchAuthenticatorMessage(data).then((res) => {
			if (!CoreHelper.isNullOrEmpty(res.data?.labelToDisplay)) {
				labelFromPhone.current = res.data?.labelToDisplay as string;
				restartTime();
				setShowAuthentication(true);
				showHideLoading();
			}
		});
	};

	const onButtonClick = useCallback(() => {
		show();
		logger('validateCode > Code is', { token: token });
		fetchSubscription().then((resp) => {
			hide();
			/* cspell: disable */
			if (
				resp.data?.transactionStatus === TransactionStatus.CODEAUTHEXPIRE ||
				resp.data?.transactionStatus === TransactionStatus.CODEAUTHINVALID ||
				resp.data?.transactionStatus === TransactionStatus.INVALIDCREDENTIALNORETRY
			) {
				/* cspell: enable */
				setShowMessageError(true);
			} else {
				conclusionIziBizi();
			}
		});
	}, [conclusionIziBizi, fetchSubscription, hide, logger, show, token]);

	const clearInformation = useCallback(() => {
		isAuthValid.current = false;
		restartTime();
		setShowAuthentication(false);
		setToken('');
		setButtonDisableValidate(true);
		setShowMessageError(false);
	}, [restartTime]);

	const onChangeValue = useCallback(
		(value: string, position: string[]) => {
			isAuthValid.current = !position.some((value, index) => {
				if (value === undefined || value === '') {
					logger('some Position', {
						position: index,
						value: value,
						isUndefined: value === undefined,
						isEmpty: value === '',
					});
					return true;
				} else {
					return false;
				}
			});

			logger('useAuthenticatorHelper > onChangeValue', { isAuthValid: isAuthValid.current });

			setButtonDisableValidate(!isAuthValid.current || !isValid);
		},
		[isValid, logger]
	);

	logger('useAuthenticatorHelper', { isAuthValid: isAuthValid.current, isValid, buttonDisableValidate });

	return {
		resources,
		canRequestNewCode,
		showAuthentication,
		buttonDisableValidate,
		showMessageError,
		loading,
		onClickToNavigate: back,
		onButtonClick,
		clearInformation,
		requestAuthentication,
		setToken,
		setButtonDisableValidate,
		onChangeValue,
	};
};
